/**
 * OfficerBadgeTile — single physical officer badge tile.
 *
 * Shows the badge image with the badge number overlaid, plus status row
 * underneath (officer name if on duty, "Available" if not, "OOS" if out
 * of service, "Activate" link for high-numbered badges 15+).
 *
 * The three small icon buttons under each tile (shield/box/person) let
 * supervisors assign the physical badge to one of three states:
 *   - On Duty       (auto, from personnel — non-clickable)
 *   - In Shadow Box (clickable)
 *   - Has Badge     (clickable — officer has it physically)
 */
import { OFFICER_BADGE } from '../../assets'
import type { Badge as BadgeType, PersonnelEntry } from '../../stores/desktop'

interface OfficerBadgeTileProps {
  badge: BadgeType
  isOos: boolean
  isActivated: boolean
  highNumbered: boolean
  officer?: PersonnelEntry
  onSetStatus: (status: BadgeType['status']) => void
  onToggleActivated: () => void
}

function StatusBtn({
  active,
  color,
  disabled,
  title,
  onClick,
  children,
}: {
  active: boolean
  color: string
  disabled?: boolean
  title: string
  onClick?: () => void
  children: React.ReactNode
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        borderRadius: 4,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        background: active ? color : '#e5e7eb',
        color: active ? '#fff' : '#9ca3af',
        width: 26,
        height: 26,
        flexShrink: 0,
        transition: 'background 0.15s',
      }}
    >
      {children}
    </button>
  )
}

export default function OfficerBadgeTile({
  badge,
  isOos,
  isActivated,
  highNumbered,
  officer,
  onSetStatus,
  onToggleActivated,
}: OfficerBadgeTileProps) {
  const notActivated = highNumbered && !isActivated
  const isOnDuty     = badge.status === 'onDuty'

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 12,
        borderRadius: 8,
        background: 'var(--bg-elevated)',
        borderLeft: isOnDuty ? '3px solid #3b82f6' : '3px solid transparent',
        opacity: isOos ? 0.5 : notActivated ? 0.4 : 1,
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 80,
          height: 112,
          background: 'var(--bg-elevated)',
          cursor: highNumbered && !isOos ? 'pointer' : 'default',
        }}
        onClick={highNumbered && !isOos && notActivated ? onToggleActivated : undefined}
      >
        <img
          src={OFFICER_BADGE}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: (isOos || notActivated) ? 'grayscale(100%) brightness(0.5)' : 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '47%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            fontWeight: 'bold',
            fontSize: 18,
            color: (isOos || notActivated) ? '#999' : '#333',
            textShadow: '0 0 2px rgba(255,255,255,0.8)',
            lineHeight: 1,
            pointerEvents: 'none',
          }}
        >
          {String(badge.id).padStart(2, '0')}
        </div>
      </div>

      <div style={{ width: '100%', marginTop: 8, textAlign: 'center' }}>
        {notActivated ? (
          <button
            onClick={onToggleActivated}
            style={{
              fontSize: 12,
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'underline',
            }}
          >
            Activate
          </button>
        ) : isOos ? (
          <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>OOS</span>
        ) : (
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: isOnDuty ? '#15803d' : '#6b7280',
            }}
          >
            {isOnDuty && officer ? officer.name : 'Available'}
          </span>
        )}
      </div>

      {!notActivated && !isOos && (
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          <StatusBtn
            active={badge.status === 'onDuty'}
            color="#22c55e"
            disabled
            title="On Duty (automatic)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </StatusBtn>

          <StatusBtn
            active={badge.status === 'inShadowBox'}
            color="#3b82f6"
            title="In Shadow Box"
            onClick={() => onSetStatus('inShadowBox')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 8v13H3V8" />
              <path d="M1 3h22v5H1z" />
              <path d="M10 12h4" />
            </svg>
          </StatusBtn>

          <StatusBtn
            active={badge.status === 'hasPhysicalBadge'}
            color="#a855f7"
            title="Officer Has Badge"
            onClick={() => onSetStatus('hasPhysicalBadge')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </StatusBtn>

          {highNumbered && isActivated && (
            <button
              title="Deactivate"
              onClick={onToggleActivated}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                borderRadius: 4,
                border: 'none',
                cursor: 'pointer',
                background: '#fee2e2',
                color: '#ef4444',
                width: 26,
                height: 26,
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
